import Component from '@glimmer/component';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { registerDestructor } from '@ember/destroyable';
import { task, timeout } from 'ember-concurrency';
import tippy from 'tippy.js';
import formatToTimeZone from '@dubbletrack/time/utils/format-to-time-zone';

// Marks an opened link as a share even where the app it opens in strips the referrer.
function withShareRef(link) {
  return `${link}${link.includes('?') ? '&' : '?'}ref=share`;
}

export default class ShareButtonComponent extends Component {
  @service navigation;
  @service intl;

  @tracked copied = false;

  constructor() {
    super(...arguments);
    registerDestructor(this, () => {
      this.tooltip?.destroy();
      this.menu?.destroy();
    });
  }

  get isLive() {
    return this.args.feed?.kind === 'broadcast';
  }

  // A position inside a live show only plays where live rewind is on.
  get offersPosition() {
    return !this.isLive || !!this.args.feed?.isRewindable;
  }

  get stationName() {
    return this.args.feed?.station?.name;
  }

  get showTitle() {
    return this.args.feed?.airing?.showTitle || this.args.feed?.show?.title;
  }

  get stationImageUrl() {
    return this.args.feed?.station?.image?.small_square?.url;
  }

  get showImageUrl() {
    let feed = this.args.feed;
    let image = feed?.airing?.image || feed?.show?.image || feed?.airing?.show?.image;
    return image?.small_square?.url;
  }

  get liveUrl() {
    return this.navigation.liveUrl();
  }

  // Shareable during the show too: it opens on the live feed until the archive can play.
  get episodeUrl() {
    return this.navigation.episodeUrl(this.args.feed);
  }


  get positionDate() {
    let startsAt = this.args.feed?.airing?.actualStartsAt || this.args.feed?.airing?.startsAt;
    if (!startsAt) {
      return null;
    }
    return new Date(new Date(startsAt).getTime() + (this.args.feed?.playheadPositionMs ?? 0));
  }

  get positionLabel() {
    if (!this.positionDate) {
      return '';
    }
    return this.intl.formatTime(this.positionDate, {
      hour: 'numeric',
      minute: 'numeric',
      timeZone: this.args.timeZone,
      locale: this.args.locale,
    });
  }

  // Station clock, whatever zone the viewer displays.
  get positionAt() {
    if (!this.positionDate) {
      return null;
    }
    return formatToTimeZone(this.positionDate, 'HH:mm:ss', {
      timeZone: this.args.feed?.station?.localTimeZone,
    });
  }

  // Tippy adopts the rendered menu element as its content, so Glimmer keeps updating it in place.
  @action
  setupMenu(element) {
    this.tooltip = tippy(element, {
      content: 'Share',
      trigger: 'mouseenter focus',
      touch: false,
      placement: 'bottom',
      appendTo: 'parent',
      onShow: () => !this.menu?.state.isVisible,
    });
    this.menu = tippy(element, {
      content: element.nextElementSibling,
      interactive: true,
      trigger: 'click',
      placement: 'top',
      arrow: false,
      theme: 'menu',
      appendTo: () => element.closest('.dubbletrack-system') ?? document.body,
      onShow: () => this.tooltip?.hide(),
    });
  }

  @action
  share(which) {
    this.menu?.hide();
    let url = {
      live: this.liveUrl,
      episode: this.episodeUrl,
      position: this.navigation.episodeUrl(this.args.feed, { at: this.positionAt }),
    }[which];
    if (!url) {
      return;
    }
    this.shareTask.perform(withShareRef(url));
    // Only the embeddable counts shares, so the staff app has no such service.
    getOwner(this).lookup('service:link-activity')?.recordShareTask.perform(which, this.args.feed?.airing?.id);
  }

  shareTask = task({ restartable: true }, async (url) => {
    await navigator.clipboard.writeText(url);
    this.copied = true;
    await timeout(2000);
    this.copied = false;
  });
}
